(() => {
  const roots = document.querySelectorAll('[data-anav]');
  if (!roots.length) return;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  roots.forEach((root) => {
    if (root.dataset.anavReady === 'true') return;
    root.dataset.anavReady = 'true';

    const bar = root.querySelector('.anav__bar');
    const openButton = root.querySelector('[data-anav-open]');
    const closeButton = root.querySelector('[data-anav-close]');
    const drawer = root.querySelector('[data-anav-drawer]');
    const overlay = root.querySelector('[data-anav-overlay]');

    const searchOpenButton = root.querySelector('[data-anav-search-open]');
    const searchCloseButton = root.querySelector('[data-anav-search-close]');
    const searchPanel = root.querySelector('[data-anav-search-panel]');
    const searchOverlay = root.querySelector('[data-anav-search-overlay]');
    const searchInput = searchPanel?.querySelector('input[type="search"]');

    let lastFocused = null;
    let searchLastFocused = null;
    let closeTimer = null;
    let searchCloseTimer = null;
    let drawerPlaceholder = null;
    let overlayPlaceholder = null;

    const mountDrawerToViewport = () => {
      if (!drawer || !overlay || drawer.dataset.anavPortaled === 'true') return;
      drawerPlaceholder = document.createComment('altoff-drawer-placeholder');
      overlayPlaceholder = document.createComment('altoff-overlay-placeholder');
      drawer.parentNode?.insertBefore(drawerPlaceholder, drawer);
      overlay.parentNode?.insertBefore(overlayPlaceholder, overlay);
      document.body.appendChild(overlay);
      document.body.appendChild(drawer);
      drawer.dataset.anavPortaled = 'true';
    };

    const restoreDrawerToHeader = () => {
      if (!drawer || !overlay || drawer.dataset.anavPortaled !== 'true') return;
      if (overlayPlaceholder?.parentNode) overlayPlaceholder.parentNode.replaceChild(overlay, overlayPlaceholder);
      if (drawerPlaceholder?.parentNode) drawerPlaceholder.parentNode.replaceChild(drawer, drawerPlaceholder);
      drawerPlaceholder = null;
      overlayPlaceholder = null;
      delete drawer.dataset.anavPortaled;
    };

    const drawerIsOpen = () => drawer?.classList.contains('is-open');
    const searchIsOpen = () => searchPanel?.classList.contains('is-open');

    const setScrolledState = () => {
      root.classList.toggle('is-scrolled', window.scrollY > 8);
    };

    const setSearchTop = () => {
      if (!bar) return;
      root.style.setProperty('--anav-search-top', `${Math.max(0, bar.getBoundingClientRect().bottom)}px`);
    };

    const lockPage = () => {
      document.documentElement.classList.add('anav-lock');
      document.body.classList.add('anav-lock');
    };

    const unlockPage = () => {
      document.documentElement.classList.remove('anav-lock');
      document.body.classList.remove('anav-lock');

      // Remove stale styles left behind by old theme builds or Shopify preview reloads.
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.touchAction = '';
    };

    const closeDrawer = ({ restoreFocus = true, immediate = false } = {}) => {
      if (!drawer || !overlay || !openButton) return;
      window.clearTimeout(closeTimer);

      drawer.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      drawer.setAttribute('aria-hidden', 'true');
      openButton.setAttribute('aria-expanded', 'false');
      unlockPage();

      const finish = () => {
        overlay.hidden = true;
        restoreDrawerToHeader();
        if (restoreFocus && lastFocused instanceof HTMLElement) lastFocused.focus({ preventScroll: true });
      };

      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else closeTimer = window.setTimeout(finish, 360);
    };

    const openDrawer = () => {
      if (!drawer || !overlay || !openButton || drawerIsOpen()) return;
      window.clearTimeout(closeTimer);
      if (searchIsOpen()) closeSearch({ restoreFocus: false, immediate: true });

      lastFocused = document.activeElement;
      mountDrawerToViewport();
      overlay.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      openButton.setAttribute('aria-expanded', 'true');
      lockPage();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          overlay.classList.add('is-visible');
          drawer.classList.add('is-open');
        });
      });

      window.setTimeout(() => closeButton?.focus({ preventScroll: true }), 80);
    };

    const closeSearch = ({ restoreFocus = true, immediate = false } = {}) => {
      if (!searchPanel || !searchOverlay || !searchOpenButton) return;
      window.clearTimeout(searchCloseTimer);

      searchPanel.classList.remove('is-open');
      searchPanel.setAttribute('aria-hidden', 'true');
      searchOpenButton.setAttribute('aria-expanded', 'false');

      const predictiveSearch = searchPanel.querySelector('predictive-search');
      if (predictiveSearch && typeof predictiveSearch.close === 'function') predictiveSearch.close();

      const finish = () => {
        searchPanel.hidden = true;
        searchOverlay.hidden = true;
        if (restoreFocus && searchLastFocused instanceof HTMLElement) {
          searchLastFocused.focus({ preventScroll: true });
        }
      };

      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else searchCloseTimer = window.setTimeout(finish, 260);
    };

    const openSearch = (event) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (!searchPanel || !searchOverlay || !searchOpenButton || searchIsOpen()) return;
      window.clearTimeout(searchCloseTimer);
      if (drawerIsOpen()) closeDrawer({ restoreFocus: false, immediate: true });

      searchLastFocused = document.activeElement;
      setSearchTop();
      searchPanel.hidden = false;
      searchOverlay.hidden = true;
      searchPanel.setAttribute('aria-hidden', 'false');
      searchOpenButton.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => searchPanel.classList.add('is-open'));
      window.setTimeout(() => searchInput?.focus({ preventScroll: true }), 90);
    };

    const trapFocus = (event, container) => {
      if (event.key !== 'Tab' || !container) return;
      const items = [...container.querySelectorAll(focusableSelector)].filter((item) => {
        return !item.hidden && item.getAttribute('aria-hidden') !== 'true' && item.getClientRects().length > 0;
      });
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    openButton?.addEventListener('click', openDrawer);
    closeButton?.addEventListener('click', () => closeDrawer());
    overlay?.addEventListener('click', () => closeDrawer());
    searchOpenButton?.addEventListener('click', openSearch);
    searchCloseButton?.addEventListener('click', () => closeSearch());
    searchOverlay?.addEventListener('click', () => closeSearch());

    drawer?.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) closeDrawer({ restoreFocus: false, immediate: true });
    });

    searchPanel?.addEventListener('click', (event) => {
      if (event.target.closest('.predictive-search a[href]')) {
        closeSearch({ restoreFocus: false, immediate: true });
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (searchIsOpen()) {
          event.preventDefault();
          closeSearch();
        } else if (drawerIsOpen()) {
          event.preventDefault();
          closeDrawer();
        }
        return;
      }

      if (searchIsOpen()) trapFocus(event, searchPanel);
      else if (drawerIsOpen()) trapFocus(event, drawer);
    });

    const resetNavigation = () => {
      closeDrawer({ restoreFocus: false, immediate: true });
      closeSearch({ restoreFocus: false, immediate: true });
      unlockPage();
    };

    window.addEventListener('pagehide', resetNavigation);
    window.addEventListener('pageshow', resetNavigation);
    document.addEventListener('shopify:section:load', resetNavigation);
    document.addEventListener('shopify:section:unload', resetNavigation);
    document.addEventListener('shopify:section:reorder', resetNavigation);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !drawerIsOpen()) unlockPage();
    });

    setScrolledState();
    setSearchTop();
    window.addEventListener('scroll', setScrolledState, { passive: true });
    window.addEventListener('resize', setSearchTop, { passive: true });
  });
})();
